import { toast } from "@/components/common/Toast"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useRateLimitContext } from "@/contexts/RateLimitContext"
import { AgentPageContextValue } from "@/features/agent-chat/context/AgentPageContext"
import {
  appendText,
  attachToolCitations,
  createTurnAccumulator,
  finalizeTurn,
  finishTool,
  startTool,
  streamingBlocks,
  TurnAccumulator,
  turnBlocks,
} from "@/features/agent-chat/engine/turnBlocks"
import { AgentAction, ToolCallResult } from "@/features/agent-chat/types"
import { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import {
  handleStreamingResponse,
  sendMessages,
} from "@/services/api/assistantChatService"
import { CHAT_CONVERSATIONS_QUERY_KEY } from "@/services/api/chatConversationService"
import { USER_LIMITS_QUERY_KEY } from "@/services/api/userService"
import {
  Chat,
  ChatCompletionResponse,
  ChatCompletionStreamResponseToolStatus,
  Message as ChatMessage,
  CitationChunkPayload,
  SourceMetadata,
  SourceOrigin,
} from "@/types/assistantChat"
import { ApiCitation } from "@/types/questionnaire/api"
import { notifyChatSendError } from "@/utils/limitErrors"
import { useQueryClient } from "@tanstack/react-query"
import { nanoid } from "nanoid"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  determineSourceOrigin,
  isCitationPayload,
  isPersistedConversationId,
  UNPERSISTED_CHAT_ID,
} from "../utils/chatUtils"

type ToolCallAction = Extract<AgentAction, { type: "tool_call" }>

/** Max continuation requests per user message (abuse guard, mirrors the old
 * bubble engine's spec §6 limit). */
const MAX_CONTINUATIONS = 5

interface UseChatSubmitProps {
  chat: Chat
  activeConversationId?: string | null
  setChat: React.Dispatch<React.SetStateAction<Chat>>
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string
  /**
   * Agent chat (actions/tool loop/page_context/attached files) only works on
   * the v2 chat endpoint — v1 doesn't stream chat.completion.action / tool-call
   * events. When true, requests are forced to v2 regardless of RAG_V2_CHAT's
   * own default, so agent chat never silently degrades to v1.
   */
  agentChatEnabled?: boolean
  /** What the user is currently viewing; sent as `page_context` with each request. */
  pageContext?: AgentPageContextValue | null
  /**
   * The language this tab renders, sent inside `page_context`: the agent quotes
   * buttons, and neither the chat's language nor shared storage says what the
   * screen shows.
   */
  uiLanguage?: string
  /**
   * Called for each agent UI action streamed alongside the reply, once this
   * turn's own message (if any) has landed in the chat, with the resulting
   * message count so any card renders positioned at that point in the thread.
   */
  onAction?: (action: AgentAction, anchorMessageCount: number) => void
  /**
   * Executes a blocking `tool_call` action in the browser. When set, a stream
   * that ends with a pending tool call resumes via a continuation request
   * carrying the tool result instead of ending the turn.
   */
  executeToolCall?: (toolCall: ToolCallAction) => Promise<ToolCallResult>
  /**
   * Called once the turn is complete, including every tool-call continuation,
   * with the resulting message count. A write tool runs mid-turn, so an
   * affordance it offers waits for the narration that explains it — and anchors
   * to it, the same way a streamed action does.
   */
  onTurnEnd?: (anchorMessageCount: number) => void
}

interface ChatSubmitMessage {
  text?: string
  files?: unknown[]
}

export interface QueuedChatMessage extends ChatSubmitMessage {
  id: string
  text: string
}

function getPersistedConversationId(
  conversationId: string | undefined | null,
): string | undefined {
  if (!isPersistedConversationId(conversationId)) {
    return undefined
  }
  return conversationId
}

/**
 * Custom hook to handle chat message submission and streaming
 */
export function useChatSubmit({
  chat,
  activeConversationId,
  setChat,
  setStreamingMessage,
  setIsLoading,
  translate,
  agentChatEnabled = false,
  pageContext,
  uiLanguage,
  onAction,
  executeToolCall,
  onTurnEnd,
}: UseChatSubmitProps) {
  const queryClient = useQueryClient()
  const chatRateLimitEnabled = useFeatureFlag("CHAT_RATE_LIMIT")
  const {
    refresh: refreshRateLimit,
    remainingMessages,
    isUnlimited,
  } = useRateLimitContext()
  const [queuedMessages, setQueuedMessages] = useState<QueuedChatMessage[]>([])
  const chatRef = useRef(chat)
  const activeConversationIdRef = useRef(activeConversationId)
  const queuedMessagesRef = useRef<QueuedChatMessage[]>([])
  const isSubmittingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const forcedQueuedMessageRef = useRef<QueuedChatMessage | null>(null)
  const activeSubmittedMessageRef = useRef<ChatMessage | null>(null)
  // Ref (not a closure value) because a continuation loop can span several
  // requests, and page context can change mid-turn.
  const pageContextRef = useRef(pageContext)
  const uiLanguageRef = useRef(uiLanguage)
  // Client-minted conversation id so the Langfuse session groups a conversation from
  // turn 1 (before the backend has assigned an id). Sent as conversation_id; the backend
  // persists under it and echoes it back via the chat.conversation event. Reset when a
  // fresh empty chat starts so the next conversation mints its own id.
  const clientConversationIdRef = useRef<string | null>(null)

  // Assigned synchronously during render — not via useEffect — so these are
  // guaranteed current the instant a caller (e.g. "start new chat") updates
  // `chat`/`activeConversationId`, with no render-to-effect-flush gap for a
  // fast-enough send to race against. That gap was exactly how "start new
  // chat" immediately followed by sending a message could reconstruct and
  // resend the OLD conversation: submitMessage's updateChat builds its next
  // state from chatRef.current, and a not-yet-flushed effect meant that ref
  // could still be the previous, persisted chat for one tick.
  chatRef.current = chat
  activeConversationIdRef.current = activeConversationId
  pageContextRef.current = pageContext
  uiLanguageRef.current = uiLanguage

  useEffect(() => {
    if (chat.id === UNPERSISTED_CHAT_ID && (chat.messages?.length ?? 0) === 0) {
      clientConversationIdRef.current = null
    }
  }, [chat.id, chat.messages?.length])

  const updateChat = useCallback(
    (updater: React.SetStateAction<Chat>) => {
      const nextChat =
        typeof updater === "function"
          ? (updater as (currentChat: Chat) => Chat)(chatRef.current)
          : updater
      chatRef.current = nextChat
      setChat(nextChat)
    },
    [setChat],
  )

  const updateQueuedMessages = useCallback(
    (updater: (previousQueue: QueuedChatMessage[]) => QueuedChatMessage[]) => {
      setQueuedMessages((previousQueue) => {
        const nextQueue = updater(previousQueue)
        queuedMessagesRef.current = nextQueue
        return nextQueue
      })
    },
    [],
  )

  const submitMessage = useCallback(
    async (message: ChatSubmitMessage | QueuedChatMessage) => {
      const text = message.text?.trim()
      if (!text) {
        isSubmittingRef.current = false
        return
      }

      if (
        chatRateLimitEnabled &&
        !isUnlimited &&
        remainingMessages !== null &&
        remainingMessages <= 0
      ) {
        toast.error(translate("AI_CHAT/RATE_LIMIT_EXCEEDED"))
        isSubmittingRef.current = false
        return
      }

      const isFirstMessage =
        !chatRef.current?.messages || chatRef.current.messages.length === 0
      trackEvent(AnalyticsEvent.CHAT_MESSAGE_SENT, {
        message_length: text.length,
        is_first_message: isFirstMessage,
        conversation_length: (chatRef.current?.messages?.length || 0) + 1,
      })

      const newMessage: ChatMessage = { role: "user", content: text }
      const messagesToSend = [...(chatRef.current.messages || []), newMessage]
      isSubmittingRef.current = true
      activeSubmittedMessageRef.current = newMessage

      updateChat((previousChat) => ({
        ...previousChat,
        messages: [...(previousChat.messages || []), newMessage],
        updated_at: new Date().toISOString(),
      }))

      const resetSubmissionState = () => {
        isSubmittingRef.current = false
        abortControllerRef.current = null
        activeSubmittedMessageRef.current = null
      }

      const processQueuedMessages = () => {
        resetSubmissionState()

        const forcedQueuedMessage = forcedQueuedMessageRef.current
        if (forcedQueuedMessage) {
          forcedQueuedMessageRef.current = null
          void submitMessage(forcedQueuedMessage)
          return
        }

        const [nextQueuedMessage, ...remainingQueue] = queuedMessagesRef.current
        if (!nextQueuedMessage) {
          return
        }

        queuedMessagesRef.current = remainingQueue
        setQueuedMessages(remainingQueue)
        void submitMessage(nextQueuedMessage)
      }

      try {
        setIsLoading(true)
        setStreamingMessage({
          role: "assistant",
          content: "",
          reasoning: "",
        })

        // Wire history for this turn; a blocking tool call appends a
        // tool_use/tool_result pair and loops back into another request
        // instead of ending the turn (continuation, spec: agent-tool-loop).
        let wireMessages = messagesToSend
        let continuations = 0
        // One accumulator for the whole turn, including every continuation
        // round-trip: a turn that hands a tool to the browser is several
        // requests but one message, and one activity panel.
        const turn = createTurnState()

        const persistedConversationId = getPersistedConversationId(
          activeConversationIdRef.current,
        )
        // Reuse the persisted id once the backend has assigned one; otherwise mint a
        // stable client id for this conversation (turn 1) and keep it for the rest of it.
        // Computed once here, before the continuation loop below, so every round-trip
        // of this turn (including tool-call continuations) shares the same id.
        if (!persistedConversationId && !clientConversationIdRef.current) {
          clientConversationIdRef.current = crypto.randomUUID()
        }
        const conversationId =
          persistedConversationId ??
          clientConversationIdRef.current ??
          undefined

        for (;;) {
          const abortController = new AbortController()
          abortControllerRef.current = abortController

          const response = await sendMessages(wireMessages, {
            webSearchEnabled: chatRef.current.webSearchEnabled ?? false,
            conversationId,
            signal: abortController.signal,
            forceV2: agentChatEnabled,
            pageContext: pageContextRef.current
              ? {
                  ...pageContextRef.current,
                  entities: {
                    ...pageContextRef.current.entities,
                    ui_language: uiLanguageRef.current,
                  },
                }
              : undefined,
          })

          if (chatRateLimitEnabled) {
            refreshRateLimit().catch(() => {
              // Silently fail - don't block the chat flow
            })
          }

          let toolCall: ToolCallAction | null = null

          if (response instanceof ReadableStream) {
            const streamResult = await handleStreamResponse(
              response,
              handleStreamingResponse,
              setStreamingMessage,
              updateChat,
              turn,
              getPersistedConversationId(activeConversationIdRef.current),
            )
            toolCall = streamResult.toolCall
            if (onAction && streamResult.agentActions.length > 0) {
              // Anchor to the message count now that this round's own message
              // (if any) has landed in chatRef.current — see handleStreamResponse.
              const anchor =
                chatRef.current.messages?.filter((m) => !m.toolContent)
                  .length ?? 0
              for (const action of streamResult.agentActions) {
                if (action.type === "tool_call") {
                  continue
                }
                onAction(action, anchor)
              }
            }
          } else {
            handleNonStreamResponse(
              response,
              updateChat,
              setIsLoading,
              setStreamingMessage,
            )
          }

          if (!toolCall || !executeToolCall) {
            break
          }
          if (continuations >= MAX_CONTINUATIONS) {
            updateChat((previousChat) => ({
              ...previousChat,
              messages: [
                ...(previousChat.messages ?? []),
                {
                  role: "assistant",
                  content:
                    "I had to stop before completing that action. Please try again.",
                },
              ],
              updated_at: new Date().toISOString(),
            }))
            break
          }
          continuations += 1

          // The browser is this tool's executor, so the backend emitted only
          // `started` — it cannot know the outcome until the continuation
          // request carries it. Closing the step here is what keeps a blocking
          // tool from sitting in the panel as permanently in flight.
          let result: ToolCallResult
          try {
            result = await executeToolCall(toolCall)
          } catch (toolError) {
            finishTool(turn.accumulator, toolCall.toolCallId, {
              failed: true,
              result: {
                error:
                  toolError instanceof Error
                    ? toolError.message
                    : String(toolError),
              },
            })
            commitTurn(updateChat, turn)
            throw toolError
          }
          finishTool(turn.accumulator, toolCall.toolCallId, {
            failed: false,
            result: result.content,
          })
          commitTurn(updateChat, turn)
          const toolMessages: ChatMessage[] = [
            {
              role: "assistant",
              content: "",
              toolContent: [
                {
                  tool_use: {
                    tool_call_id: toolCall.toolCallId,
                    name: toolCall.name,
                    input: toolCall.input,
                  },
                },
              ],
            },
            {
              role: "user",
              content: "",
              toolContent: [
                {
                  tool_result: {
                    tool_call_id: result.toolCallId,
                    content: result.content,
                  },
                },
              ],
            },
          ]
          wireMessages = [...wireMessages, ...toolMessages]
          updateChat((previousChat) => ({
            ...previousChat,
            messages: [...(previousChat.messages ?? []), ...toolMessages],
            updated_at: new Date().toISOString(),
          }))
        }

        setIsLoading(false)
        onTurnEnd?.(
          chatRef.current.messages?.filter((m) => !m.toolContent).length ?? 0,
        )
        processQueuedMessages()
        void queryClient
          .invalidateQueries({
            queryKey: CHAT_CONVERSATIONS_QUERY_KEY,
          })
          .catch(() => {
            // Silently fail - don't block the chat flow
          })
        // Keep the sidebar usage numbers fresh after a counted message
        void queryClient.invalidateQueries({ queryKey: USER_LIMITS_QUERY_KEY })
      } catch (err) {
        if (
          (err instanceof DOMException && err.name === "AbortError") ||
          (err instanceof Error && err.name === "AbortError")
        ) {
          setStreamingMessage(null)
          setIsLoading(false)
          processQueuedMessages()
          return
        }

        notifyChatSendError(err, translate)
        void queryClient.invalidateQueries({ queryKey: USER_LIMITS_QUERY_KEY })
        setStreamingMessage(null)
        setIsLoading(false)
        resetSubmissionState()
      }
    },
    [
      chatRateLimitEnabled,
      isUnlimited,
      remainingMessages,
      translate,
      agentChatEnabled,
      updateChat,
      setIsLoading,
      setStreamingMessage,
      refreshRateLimit,
      queryClient,
      onAction,
      executeToolCall,
      onTurnEnd,
    ],
  )

  const discardActiveSubmittedMessage = useCallback(() => {
    const activeSubmittedMessage = activeSubmittedMessageRef.current
    if (!activeSubmittedMessage) {
      return
    }

    updateChat((previousChat) => {
      const activeMessageIndex = previousChat.messages?.lastIndexOf(
        activeSubmittedMessage,
      )

      if (activeMessageIndex === undefined || activeMessageIndex < 0) {
        return previousChat
      }

      return {
        ...previousChat,
        messages: previousChat.messages?.filter(
          (_, index) => index !== activeMessageIndex,
        ),
        updated_at: new Date().toISOString(),
      }
    })
  }, [updateChat])

  const handleSubmit = useCallback(
    async (message: ChatSubmitMessage) => {
      const text = message.text?.trim()
      if (!text) {
        return
      }

      if (isSubmittingRef.current) {
        updateQueuedMessages((previousQueue) => [
          ...previousQueue,
          {
            id: nanoid(),
            text,
            files: message.files,
          },
        ])
        return
      }

      await submitMessage({ text, files: message.files })
    },
    [submitMessage, updateQueuedMessages],
  )

  const removeQueuedMessage = useCallback(
    (messageId: string) => {
      updateQueuedMessages((previousQueue) =>
        previousQueue.filter((message) => message.id !== messageId),
      )
    },
    [updateQueuedMessages],
  )

  const forceSendQueuedMessage = useCallback(
    (messageId: string) => {
      const queuedMessage = queuedMessagesRef.current.find(
        (message) => message.id === messageId,
      )
      if (!queuedMessage) {
        return
      }

      const remainingQueue = queuedMessagesRef.current.filter(
        (message) => message.id !== messageId,
      )
      queuedMessagesRef.current = remainingQueue
      setQueuedMessages(remainingQueue)

      if (isSubmittingRef.current) {
        discardActiveSubmittedMessage()
        forcedQueuedMessageRef.current = queuedMessage
        abortControllerRef.current?.abort()
        return
      }

      void submitMessage(queuedMessage)
    },
    [discardActiveSubmittedMessage, submitMessage],
  )

  return {
    handleSubmit,
    queuedMessages,
    removeQueuedMessage,
    forceSendQueuedMessage,
  }
}

/**
 * Everything one CHAT TURN accumulates, shared across its rounds.
 *
 * A turn that hands a tool to the browser spans several HTTP requests but is
 * one message to the user, so this lives above the request loop rather than
 * inside the stream handler — that is what keeps a multi-round turn to a
 * single activity panel instead of one per round.
 */
interface TurnState {
  /** Identifies this turn's assistant message for later rounds to update. */
  id: string
  accumulator: TurnAccumulator
  citations: ApiCitation[]
  sourceMetadata?: SourceMetadata
  sourceOrigin: SourceOrigin
  reasoning: string
  reasoningDuration?: number
  reasoningStartTime?: number
  role: "assistant" | "system"
}

function createTurnState(): TurnState {
  return {
    id: nanoid(),
    accumulator: createTurnAccumulator(),
    citations: [],
    sourceOrigin: "none",
    reasoning: "",
    role: "assistant",
  }
}

/** The turn as a chat message. `content` stays the flat concatenation of every
 * text chunk so copy/export keep seeing the whole turn; `blocks` is the
 * activity/answer split the renderer prefers when present. */
function turnMessage(turn: TurnState): ChatMessage {
  return {
    role: turn.role,
    content: turn.accumulator.flatText,
    reasoning: turn.reasoning || undefined,
    reasoningDuration: turn.reasoningDuration,
    citations: turn.citations,
    sourceMetadata: turn.sourceMetadata,
    sourceOrigin: turn.sourceOrigin,
    blocks: turnBlocks(turn.accumulator),
    turnId: turn.id,
  } as ChatMessage
}

/** Append this turn's message, or update it if an earlier round already
 * appended one. Found by `turnId`, not by position: `toolContent`
 * pseudo-messages are appended between rounds. */
function commitTurn(
  setChat: React.Dispatch<React.SetStateAction<Chat>>,
  turn: TurnState,
): void {
  const message = turnMessage(turn)
  setChat((prevChat) => {
    const messages = prevChat.messages ?? []
    const index = messages.findIndex((m) => m.turnId === turn.id)
    return {
      ...prevChat,
      messages:
        index === -1
          ? [...messages, message]
          : [
              ...messages.slice(0, index),
              message,
              ...messages.slice(index + 1),
            ],
      updated_at: new Date().toISOString(),
    }
  })
}

/**
 * Handle streaming response from the API
 */
async function handleStreamResponse(
  response: ReadableStream,
  handleStreamingResponse: (
    stream: ReadableStream,
    onChunk: (
      content: string | ApiCitation[] | CitationChunkPayload,
      role?: "assistant" | "system" | "citation" | "reasoning",
    ) => void,
    onDone: () => void,
    onConversationId?: (conversationId: string) => void,
    onAction?: (action: unknown) => void,
    onToolStatus?: (status: ChatCompletionStreamResponseToolStatus) => void,
  ) => Promise<void>,
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>,
  setChat: React.Dispatch<React.SetStateAction<Chat>>,
  turn: TurnState,
  currentConversationId?: string,
): Promise<{ toolCall: ToolCallAction | null; agentActions: AgentAction[] }> {
  let capturedToolCall: ToolCallAction | null = null
  const agentActions: AgentAction[] = []

  const publishStreaming = () => {
    setStreamingMessage({
      role: turn.role,
      content: turn.accumulator.flatText,
      reasoning: turn.reasoning || undefined,
      reasoningDuration: turn.reasoningDuration,
      citations: turn.citations,
      sourceMetadata: turn.sourceMetadata,
      sourceOrigin: turn.sourceOrigin,
      blocks: streamingBlocks(turn.accumulator),
      // The accumulator is CUMULATIVE across every round of this turn, so a
      // continuation round's first chunk republishes round 1's narration and
      // tool groups too — which a blocking tool (e.g. draft_or_revise_answers)
      // already committed into chat.messages at the end of round 1. Tagging
      // the streaming bubble with the same id is what lets the render layer
      // hide that now-redundant committed copy instead of showing both (see
      // ChatInterface.tsx's `visibleMessages`).
      turnId: turn.id,
    } as ChatMessage)
  }

  await handleStreamingResponse(
    response,
    (content, role) => {
      if (role === "assistant" || role === "system") {
        turn.role = role
      }
      if (role === "reasoning") {
        if (turn.reasoningStartTime === undefined) {
          turn.reasoningStartTime = Date.now()
        }
        turn.reasoning += content
        publishStreaming()
        return
      }
      if (role === "citation") {
        if (isCitationPayload(content)) {
          turn.citations.splice(0, turn.citations.length, ...content.citations)
          turn.sourceMetadata = content.metadata
          if (content.toolCallId) {
            attachToolCitations(
              turn.accumulator,
              content.toolCallId,
              content.citations,
            )
          }
        } else {
          turn.citations.push(...(content as ApiCitation[]))
        }
        turn.sourceOrigin = determineSourceOrigin(
          turn.citations,
          turn.sourceMetadata,
        )
        publishStreaming()
        return
      }
      // First text chunk closes the reasoning timer (v1 RAG path only — the
      // agent loop streams no reasoning, see the activity design).
      if (
        turn.reasoningStartTime !== undefined &&
        turn.reasoningDuration === undefined
      ) {
        turn.reasoningDuration = Math.ceil(
          (Date.now() - turn.reasoningStartTime) / 1000,
        )
      }
      appendText(turn.accumulator, content as string)
      turn.sourceOrigin = determineSourceOrigin(
        turn.citations,
        turn.sourceMetadata,
      )
      publishStreaming()
    },
    () => {
      // Only the LAST round of the turn finalizes. A round that ends by
      // handing a tool to the browser is still mid-turn, and closing its tool
      // groups there would stop the next round from joining them.
      if (!capturedToolCall) {
        finalizeTurn(turn.accumulator)
      }
      if (turn.accumulator.flatText || turnBlocks(turn.accumulator)) {
        commitTurn(setChat, turn)
      }
      setStreamingMessage(null)
    },
    (conversationId) => {
      if (conversationId && conversationId !== currentConversationId) {
        setChat((prevChat) => ({
          ...prevChat,
          id: conversationId,
          updated_at: new Date().toISOString(),
        }))
      }
    },
    (action) => {
      const candidate = action as AgentAction | null | undefined
      if (candidate?.type === "tool_call") {
        capturedToolCall = candidate
      }
      if (candidate) {
        // Buffered rather than dispatched inline: any card this produces
        // (proposal/navigate) needs to render after this turn's own message,
        // which doesn't land in chat.messages until the completion handler
        // above runs — the caller flushes these once this call resolves.
        agentActions.push(candidate)
      }
    },
    (status) => {
      if (status.phase === "started") {
        startTool(turn.accumulator, {
          toolCallId: status.tool_call_id,
          tool: status.tool,
          input: status.input,
        })
      } else {
        finishTool(turn.accumulator, status.tool_call_id, {
          failed: status.phase === "failed",
          result: status.result,
        })
      }
      publishStreaming()
    },
  )

  return { toolCall: capturedToolCall, agentActions }
}

/**
 * Handle non-streaming response from the API
 */
function handleNonStreamResponse(
  response: ChatCompletionResponse,
  setChat: React.Dispatch<React.SetStateAction<Chat>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>,
) {
  const aiResponse: ChatMessage = {
    ...response.choices[0].message,
  }
  aiResponse.sourceOrigin = determineSourceOrigin(aiResponse.citations)
  setChat((prevChat) => ({
    ...prevChat,
    id: response.conversation_id || prevChat.id,
    messages: [...(prevChat.messages ?? []), aiResponse],
    updated_at: new Date().toISOString(),
  }))
  setStreamingMessage(null)
  setIsLoading(false)
}
